import { Navigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { resolveOrdersSettingsError } from "utils/orders-settings-errors";
import { updateOrdersSettings } from "services/company.service";
import { SettingsCard } from "./ui";
import { resolveTemplateSource } from "./template-slot-card";
import { DocumentSlot, DocumentSlotRow } from "./document-slot-row";
import { InvoiceSeriesCard } from "./invoice-series-card";
import { useSettingNames } from "./use-setting-names";

interface OrdersSettingsContainerProps {
  companyEntity: any;
  /** Loads the company again; a save is done when that has finished. */
  onRefresh: () => Promise<unknown>;
}

export default function OrdersSettingsContainer({
  companyEntity,
  onRefresh,
}: OrdersSettingsContainerProps) {
  const { t } = useTranslation();
  const { companyId = "" } = useParams<{ companyId: string }>();
  const [searchParams] = useSearchParams();
  const names = useSettingNames(companyId);

  // Old links to the order amendment tab (?sub=order-amendment) land on its row.
  const opensOrderAmendment = searchParams.get("sub") === "order-amendment";

  const salesInvoice: DocumentSlot = {
    source: resolveTemplateSource(
      companyEntity.salesInvoiceTemplateSource,
      companyEntity.salesInvoiceTemplate
    ),
    template: companyEntity.salesInvoiceTemplate || "",
    series: companyEntity.salesInvoiceSeries || "",
    fileType: companyEntity.salesInvoiceFileType || "",
    category: companyEntity.salesInvoiceCategory || "",
  };
  const salesInvoiceNumberSeries = companyEntity.salesInvoiceNumberSeries || "";
  const orderAmendment: DocumentSlot = {
    source: resolveTemplateSource(
      companyEntity.orderAmendmentTemplateSource,
      companyEntity.orderAmendmentTemplate
    ),
    template: companyEntity.orderAmendmentTemplate || "",
    series: companyEntity.orderAmendmentSeries || "",
    fileType: companyEntity.orderAmendmentFileType || "",
    category: companyEntity.orderAmendmentCategory || "",
  };

  // The server saves a slot as a whole and leaves alone a slot that is not
  // sent, so each save sends the one slot it changes, complete.
  const saveSalesInvoice = (slot: DocumentSlot, numberSeries: string) =>
    updateOrdersSettings(
      {
        salesInvoiceTemplate: slot.template,
        salesInvoiceTemplateSource: slot.source,
        salesInvoiceSeries: slot.series,
        salesInvoiceNumberSeries: numberSeries,
        salesInvoiceFileType: slot.fileType,
        salesInvoiceCategory: slot.category,
      },
      companyId
    ).then(onRefresh);

  const saveOrderAmendment = (slot: DocumentSlot) =>
    updateOrdersSettings(
      {
        orderAmendmentTemplate: slot.template,
        orderAmendmentTemplateSource: slot.source,
        orderAmendmentSeries: slot.series,
        orderAmendmentFileType: slot.fileType,
        orderAmendmentCategory: slot.category,
      },
      companyId
    ).then(onRefresh);

  // Codes come back as a bare string body or as { message } / { code } depending
  // on the endpoint; the resolver turns any of them into a sentence to act on.
  const saveErrorMessage = (err: unknown) =>
    resolveOrdersSettingsError(err, t).message;

  return (
    <div className="space-y-5">
      <InvoiceSeriesCard
        companyId={companyId}
        selectedSeriesId={salesInvoiceNumberSeries}
        saveErrorMessage={saveErrorMessage}
        onSelect={(seriesId) => saveSalesInvoice(salesInvoice, seriesId)}
      />

      <SettingsCard
        title={t("settings_sales_documents_card")}
        description={t("settings_sales_documents_hint")}
      >
        <DocumentSlotRow
          label={t("settings_sales_document_invoice")}
          customTemplateWarning={t("sales_invoice_custom_template_warning")}
          slot={salesInvoice}
          names={names}
          saveErrorMessage={saveErrorMessage}
          onSave={(slot) => saveSalesInvoice(slot, salesInvoiceNumberSeries)}
        />
        <DocumentSlotRow
          label={t("settings_sales_document_amendment")}
          customTemplateWarning={t("order_amendment_custom_template_warning")}
          slot={orderAmendment}
          names={names}
          openOnLoad={opensOrderAmendment}
          saveErrorMessage={saveErrorMessage}
          onSave={saveOrderAmendment}
        />
      </SettingsCard>

      <p className="text-sm text-gray-500">
        {t("sales_invoice_order_schedule_hint")}
      </p>
    </div>
  );
}

export function OrdersSettingsRedirect() {
  const { companyId } = useParams<{ companyId: string }>();
  return <Navigate to={`/app/${companyId}/settings/sales`} replace />;
}

export function OrderAmendmentSettingsRedirect() {
  const { companyId } = useParams<{ companyId: string }>();
  return (
    <Navigate
      to={`/app/${companyId}/settings/sales?sub=order-amendment`}
      replace
    />
  );
}
